import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Skeleton } from "../components/ui/skeleton";
import { ArrowLeft, Calendar } from "lucide-react";
import { format } from "date-fns";

export default function BlogDetailPage() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    setError(false);
    
    api.get(`/blogs/${slug}`)
      .then(res => {
        setBlog(res.data);
        document.title = `${res.data.seo_title || res.data.title} — YoursDreamDecoration`;
      })
      .catch(err => {
        console.error(err);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-24 pb-12 max-w-3xl mx-auto px-4">
        <Skeleton className="w-full h-10 mb-4" />
        <Skeleton className="w-1/3 h-6 mb-12" />
        <Skeleton className="w-full aspect-video rounded-3xl mb-8" />
        <Skeleton className="w-full h-4 mb-2" />
        <Skeleton className="w-full h-4 mb-2" />
        <Skeleton className="w-3/4 h-4" />
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center pt-20">
        <h1 className="text-3xl font-serif-display text-slate-900 mb-4">Post Not Found</h1>
        <p className="text-slate-500 mb-8">The article you are looking for does not exist.</p>
        <Link to="/blog" className="text-primary font-bold hover:underline">
          ← Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-24 pb-20">
      <article className="max-w-3xl mx-auto px-4">
        
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-bold text-slate-400 hover:text-primary transition-colors mb-8 uppercase tracking-widest">
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>

        <header className="mb-12">
          <h1 className="font-serif-display text-4xl md:text-5xl text-slate-900 leading-tight mb-6">
            {blog.title}
          </h1>
          <div className="flex items-center gap-4 text-slate-500 text-sm">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {format(new Date(blog.created_at), "MMMM dd, yyyy")}
            </span>
            <span>•</span>
            <span>5 min read</span>
          </div>
        </header>

        {blog.image && (
          <div className="mb-12 rounded-[2rem] overflow-hidden shadow-2xl shadow-slate-200">
            <img 
              src={blog.image} 
              alt={blog.title} 
              className="w-full h-auto aspect-video object-cover"
            />
          </div>
        )}

        <div className="prose prose-lg prose-slate prose-headings:font-serif-display prose-headings:text-slate-900 prose-a:text-primary hover:prose-a:text-primary/80 prose-img:rounded-3xl max-w-none">
            {/* Simple markdown parsing for the AI generated blog */}
            {blog.content.split('\n').map((paragraph, idx) => {
              if (paragraph.startsWith('### ')) {
                return <h3 key={idx}>{paragraph.replace('### ', '')}</h3>;
              }
              if (paragraph.startsWith('## ')) {
                return <h2 key={idx} className="text-3xl mt-12 mb-6">{paragraph.replace('## ', '')}</h2>;
              }
              if (paragraph.startsWith('# ')) {
                return null; // Skip H1 as it's already in the header
              }
              if (paragraph.startsWith('- ') || paragraph.startsWith('* ')) {
                return <li key={idx} className="ml-4 list-disc">{paragraph.substring(2)}</li>;
              }
              if (paragraph.trim() === '') return <br key={idx} />;
              
              // Handle bold text
              const parts = paragraph.split(/(\*\*.*?\*\*)/g);
              
              return (
                <p key={idx}>
                  {parts.map((part, i) => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                      return <strong key={i}>{part.slice(2, -2)}</strong>;
                    }
                    return part;
                  })}
                </p>
              );
            })}
        </div>

      </article>
    </div>
  );
}
